import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import { IconSearch, IconX, IconCircleCheck } from '@tabler/icons-react';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildTechArticleSchema,
} from '@/lib/seo';
import {
  changelogEntries,
  type ChangeCategory,
  type ChangeType,
  type ChangelogEntry,
} from './changelog-data';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { scrollToElement } from '@/components/marketing/use-smooth-scroll';
import { PageHero } from '@/components/marketing/warm/page-hero';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import { Btn, Chip, Muted, useReveal } from '@/components/marketing/warm/primitives';

type Tone = Parameters<typeof Chip>[0]['tone'];

const TYPE_CHIP: Record<ChangeType, { label: string; tone: Tone }> = {
  new: { label: 'New', tone: 'green' },
  improved: { label: 'Improved', tone: 'blue' },
  fixed: { label: 'Fixed', tone: 'amber' },
  breaking: { label: 'Breaking', tone: 'red' },
  deprecated: { label: 'Deprecated', tone: 'neutral' },
};

const CATEGORY_CHIP: Record<ChangeCategory, { label: string; tone: Tone }> = {
  product: { label: 'Product', tone: 'neutral' },
  api: { label: 'API', tone: 'dark' },
  security: { label: 'Security', tone: 'red' },
};

type FilterTab = 'all' | ChangeCategory;

const TABS: { value: FilterTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'product', label: 'Product' },
  { value: 'api', label: 'API' },
  { value: 'security', label: 'Security' },
];

function TimelineEntry({ entry }: { entry: ChangelogEntry }) {
  const ref = useReveal<HTMLLIElement>();
  const date = new Date(entry.date);
  return (
    <li ref={ref} id={entry.id} className="home-reveal relative grid md:grid-cols-[140px_1fr] gap-x-10 gap-y-2 pb-10">
      <div className="md:text-right md:pt-5 pl-7 md:pl-0">
        <time dateTime={entry.date} className="text-sm" style={{ color: 'var(--warm-muted)' }}>{format(date, 'MMM d, yyyy')}</time>
      </div>
      <span aria-hidden="true" className="absolute left-0 md:left-[156px] top-1.5 md:top-6 w-[11px] h-[11px] rounded-full" style={{ background: 'var(--warm-page)', border: '2px solid var(--warm-text)' }} />
      <article className="ml-7 md:ml-0 rounded-2xl p-6" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)' }}>
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={TYPE_CHIP[entry.type].tone}>{TYPE_CHIP[entry.type].label}</Chip>
          <Chip tone={CATEGORY_CHIP[entry.category].tone}>{CATEGORY_CHIP[entry.category].label}</Chip>
        </div>
        <h3 className="mt-3 font-display font-light text-2xl leading-snug" style={{ color: 'var(--warm-text)' }}>
          <a href={`#${entry.id}`} className="hover:underline decoration-1 underline-offset-4">{entry.title}</a>
        </h3>
        <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>{entry.description}</p>
        {entry.items && entry.items.length > 0 && (
          <ul className="mt-4 space-y-2">
            {entry.items.map((item, i) => (
              <li key={i} className="flex gap-2.5 text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
                <IconCircleCheck size={16} stroke={1.75} className="shrink-0 mt-0.5" style={{ color: 'var(--warm-ok)' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
        {entry.link && (
          <Link to={entry.link.href} className="mt-4 inline-flex items-center gap-1 text-sm font-medium" style={{ color: 'var(--warm-text)' }}>
            {entry.link.label} <ArrowRight className="size-3.5" />
          </Link>
        )}
      </article>
    </li>
  );
}

export function ChangelogPage() {
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    // Clear the fixed nav (64px) plus the sticky filter bar.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) scrollToElement(el, -140);
    }, 350);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const title = 'Kora Changelog — Product updates and new features';
    const description = 'Kora changelog — product updates, new features, API improvements, and security enhancements.';
    setDocumentMeta(title, description);
    setCanonical('/changelog');

    const latestDate = changelogEntries.length > 0
      ? changelogEntries[changelogEntries.length - 1].date
      : new Date().toISOString();

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/changelog', title, description),
      buildTechArticleSchema(
        'Kora Changelog',
        description,
        latestDate,
        latestDate,
      ),
    ]);

    return cleanup;
  }, []);

  const counts = useMemo(
    () => ({
      all: changelogEntries.length,
      product: changelogEntries.filter((e) => e.category === 'product').length,
      api: changelogEntries.filter((e) => e.category === 'api').length,
      security: changelogEntries.filter((e) => e.category === 'security').length,
    }),
    [],
  );

  const filtered = useMemo(() => {
    const sorted = [...changelogEntries].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
    const byCategory =
      filter === 'all' ? sorted : sorted.filter((e) => e.category === filter);
    if (!search.trim()) return byCategory;
    const q = search.toLowerCase();
    return byCategory.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.items?.some((item) => item.toLowerCase().includes(q)),
    );
  }, [filter, search]);

  const groupedByYear = useMemo(() => {
    const groups: { year: number; entries: ChangelogEntry[] }[] = [];
    for (const entry of filtered) {
      const year = new Date(entry.date).getFullYear();
      const last = groups[groups.length - 1];
      if (last && last.year === year) {
        last.entries.push(entry);
      } else {
        groups.push({ year, entries: [entry] });
      }
    }
    return groups;
  }, [filtered]);

  return (
    <MarketingShell>
      <PageHero
        eyebrow="Changelog"
        title={<>What's new <br /><Muted><span className="italic">in Kora</span></Muted></>}
        lead="Updates to the product and API, shipped continuously. Follow along as we build the best issue tracker for modern ops teams."
        actions={<>
          <Btn to="/roadmap">See the roadmap <ArrowRight className="size-3.5" /></Btn>
          <Link to="/docs" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm" style={{ color: 'var(--warm-muted)' }}>Browse the API docs →</Link>
        </>}
      />

      <section className="pb-24">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <div className="sticky top-16 z-20 -mx-5 sm:-mx-8 px-5 sm:px-8 py-3 mb-8 flex flex-col sm:flex-row sm:items-center gap-3"
            style={{ background: 'var(--warm-nav)', backdropFilter: 'blur(10px)', borderBottom: '1px solid var(--warm-border)' }}>
            <div role="tablist" aria-label="Filter changelog" className="flex items-center gap-1.5 overflow-x-auto">
              {TABS.map((tab) => {
                const on = filter === tab.value;
                return (
                  <button key={tab.value} role="tab" aria-selected={on} onClick={() => setFilter(tab.value)}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors"
                    style={on ? { background: 'var(--warm-inv)', color: 'var(--warm-inv-text)' } : { background: 'var(--warm-card)', color: 'var(--warm-muted)', border: '1px solid var(--warm-border)' }}>
                    {tab.label}<span className="text-xs tabular-nums" style={{ opacity: on ? 0.7 : 1 }}>{counts[tab.value]}</span>
                  </button>
                );
              })}
            </div>
            <label className="relative sm:ml-auto sm:w-64">
              <span className="sr-only">Search changelog</span>
              <IconSearch size={16} stroke={1.75} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--warm-muted)' }} />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search changelog..."
                className="w-full rounded-full pl-9 pr-8 py-2 text-sm outline-none"
                style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)', color: 'var(--warm-text)' }} />
              {search && (
                <button onClick={() => setSearch('')} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--warm-muted)' }}>
                  <IconX size={14} stroke={2} />
                </button>
              )}
            </label>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-2xl p-12 text-center" style={{ border: '1px dashed var(--warm-border)' }}>
              <p className="text-sm" style={{ color: 'var(--warm-muted)' }}>
                {search ? `No results for "${search}".` : 'No entries in this category yet.'}
              </p>
            </div>
          ) : (
            <div className="relative">
              <span aria-hidden="true" className="absolute left-[5px] md:left-[161px] top-2 bottom-0 w-px" style={{ background: 'var(--warm-border)' }} />
              {groupedByYear.map((group) => (
                <div key={group.year}>
                  <div className="relative grid md:grid-cols-[140px_1fr] gap-x-10 pb-6">
                    <h2 className="pl-7 md:pl-0 md:text-right font-display font-light text-3xl" style={{ color: 'var(--warm-text)' }}>{group.year}</h2>
                  </div>
                  <ol>
                    {group.entries.map((entry) => <TimelineEntry key={entry.id} entry={entry} />)}
                  </ol>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaBand />
    </MarketingShell>
  );
}
